import styles from './password-strength-indicator.scss';
import React, { FC } from 'react';
import classnames from 'classnames/bind';
import { PasswordFactor, PasswordFactorStatus } from '@/constants/user';
import {
  PASSWORD_REGEX_CAPITAL,
  PASSWORD_REGEX_LENGTH,
  PASSWORD_REGEX_LOWER,
  PASSWORD_REGEX_NUMBER,
  PASSWORD_REGEX_SYMBOL,
  PASSWORD_SINGLE_CHAR,
} from '@/constants/system';
const cx = classnames.bind(styles);
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';

type PasswordStrengthIndicatorProps = {
  password: string;
  showError: boolean;
};

const PasswordStrengthIndicator: FC<PasswordStrengthIndicatorProps> = ({
  password,
  showError,
}) => {
  const { lt } = useLevvelTranslation();
  const passwordFactors: PasswordFactor[] = [
    {
      labelKey: 'auth_reset_pass_factor_capital',
      rule: PASSWORD_REGEX_CAPITAL,
      status: PasswordFactorStatus.default,
    },
    {
      labelKey: 'auth_reset_pass_factor_lower',
      rule: PASSWORD_REGEX_LOWER,
      status: PasswordFactorStatus.default,
    },
    {
      labelKey: 'auth_reset_pass_factor_symbol',
      rule: PASSWORD_REGEX_SYMBOL,
      status: PasswordFactorStatus.default,
    },
    {
      labelKey: 'auth_reset_pass_factor_number',
      rule: PASSWORD_REGEX_NUMBER,
      status: PasswordFactorStatus.default,
    },
    {
      labelKey: 'auth_reset_pass_factor_length',
      rule: PASSWORD_REGEX_LENGTH,
      status: PasswordFactorStatus.default,
    },
  ];

  passwordFactors.forEach(f => {
    if (f.rule.test(password)) {
      f.status = PasswordFactorStatus.pass;
    } else {
      f.status = showError
        ? PasswordFactorStatus.error
        : PasswordFactorStatus.default;
    }
  });

  const invalidChars = [];
  for (let i = 0; i < password.length; i++) {
    const char = password.charAt(i);
    if (!PASSWORD_SINGLE_CHAR.test(char)) {
      invalidChars.push(char);
    }
  }

  return (
    <ul>
      {passwordFactors.map((f, i) => (
        <li
          key={i}
          className={cx([
            PasswordFactorStatus[f.status],
            styles.passwordFactorsItem,
          ])}>
          {f.status === PasswordFactorStatus.default && <span>•</span>}
          {f.status === PasswordFactorStatus.pass && (
            <span className={cx(['iconfont icon-ico-done'])} />
          )}
          {f.status === PasswordFactorStatus.error && (
            <span className={cx(['iconfont icon-ico-delete'])} />
          )}
          &nbsp;<span>{lt(f.labelKey)}</span>
        </li>
      ))}
      {invalidChars.length > 0 && (
        <li
          className={cx([
            PasswordFactorStatus[PasswordFactorStatus.error],
            styles.passwordFactorsItem,
          ])}>
          <span className={cx(['iconfont icon-ico-delete'])} />
          &nbsp;
          <span>
            {lt('auth_reset_pass_invalid_chars')}: {invalidChars.join(', ')}
          </span>
        </li>
      )}
    </ul>
  );
};

export default PasswordStrengthIndicator;
